import { formatCurrency } from '@/utils/formatCurrency';
import type {
  SpAddedKeyword,
  SpAddedProductTarget,
  SpAdvertisedProduct,
  SpBiddingStrategy,
  SpCategorySuggestion,
  SpKeywordSuggestionRow,
  SpMatchType,
  SpNegativeMatchType,
} from '@/types/spCampaignCreate';

export const SP_PLACEMENT_AMAZON: Record<
  'top_of_search' | 'rest_of_search' | 'product_pages',
  'PLACEMENT_TOP' | 'PLACEMENT_REST_OF_SEARCH' | 'PLACEMENT_PRODUCT_PAGE'
> = {
  top_of_search: 'PLACEMENT_TOP',
  rest_of_search: 'PLACEMENT_REST_OF_SEARCH',
  product_pages: 'PLACEMENT_PRODUCT_PAGE',
};

export function parsePercentInput(raw: string): number | null {
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n <= 0) return null;
  return Math.round(n);
}

export function buildPlacementBidding(input: {
  topOfSearch: string;
  restOfSearch: string;
  productPages: string;
}): Array<{ placement: string; percentage: number }> {
  const out: Array<{ placement: string; percentage: number }> = [];
  const pairs: Array<[string, keyof typeof SP_PLACEMENT_AMAZON]> = [
    [input.topOfSearch, 'top_of_search'],
    [input.restOfSearch, 'rest_of_search'],
    [input.productPages, 'product_pages'],
  ];
  for (const [raw, key] of pairs) {
    const pct = parsePercentInput(raw);
    if (pct == null) continue;
    out.push({ placement: SP_PLACEMENT_AMAZON[key], percentage: pct });
  }
  return out;
}

export function buildDynamicBidding(
  strategy: SpBiddingStrategy,
  placements: { topOfSearch: string; restOfSearch: string; productPages: string }
): Record<string, unknown> {
  const placementBidding = buildPlacementBidding(placements);
  const dynamicBidding: Record<string, unknown> = { strategy };
  if (placementBidding.length > 0) {
    dynamicBidding.placementBidding = placementBidding;
  }
  return dynamicBidding;
}

export function keywordRowKey(keywordText: string, matchType: string): string {
  return `${keywordText.trim().toLowerCase()}::${matchType.toUpperCase()}`;
}

export function productTargetRowKey(expressionType: string, value: string): string {
  return `${expressionType}::${value}`;
}

export function advertisedProductKey(product: Pick<SpAdvertisedProduct, 'asin' | 'sku'>): string {
  return product.sku ? `sku:${product.sku}` : `asin:${product.asin}`;
}

export function filterAdvertisedProducts(
  products: SpAdvertisedProduct[],
  query: string
): SpAdvertisedProduct[] {
  const q = query.trim().toLowerCase();
  if (!q) return products;
  return products.filter((product) => {
    const fields = [product.title, product.asin, product.sku];
    return fields.some((field) => field?.toLowerCase().includes(q));
  });
}

export function isAdvertisedProductIneligible(product: SpAdvertisedProduct): boolean {
  const status = product.eligibilityStatus?.toUpperCase();
  return Boolean(status) && status !== 'ELIGIBLE';
}

export function applyEligibilityStatuses(
  products: SpAdvertisedProduct[],
  elig: Map<string, string>
): SpAdvertisedProduct[] {
  if (elig.size === 0) return products;
  return products.map((product) => {
    const status = elig.get(product.asin.toUpperCase());
    return status ? { ...product, eligibilityStatus: status } : product;
  });
}

export function parseKeywordList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of raw.split(/[\n,]+/)) {
    const text = line.trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

export function expandKeywordsForMatchTypes(
  texts: string[],
  matchTypes: SpMatchType[],
  suggestedByKey: Map<string, number | null> = new Map()
): SpAddedKeyword[] {
  const types = matchTypes.length > 0 ? matchTypes : (['EXACT'] as SpMatchType[]);
  const rows: SpAddedKeyword[] = [];
  for (const keywordText of texts) {
    for (const matchType of types) {
      const suggestedBid = suggestedByKey.get(keywordRowKey(keywordText, matchType)) ?? null;
      rows.push({
        keywordText,
        matchType,
        suggestedBid,
        bid: suggestedBid != null ? String(suggestedBid) : '',
      });
    }
  }
  return rows;
}

export function mergeAddedKeywords(
  existing: SpAddedKeyword[],
  incoming: SpAddedKeyword[]
): { next: SpAddedKeyword[]; added: number } {
  const keys = new Set(existing.map((row) => keywordRowKey(row.keywordText, row.matchType)));
  const next = [...existing];
  let added = 0;
  for (const row of incoming) {
    const key = keywordRowKey(row.keywordText, row.matchType);
    if (keys.has(key)) continue;
    keys.add(key);
    next.push(row);
    added += 1;
  }
  return { next, added };
}

/** Amazon ranks at most 100 user-selected keyword targets per recommendations request. */
export const KEYWORD_RECOMMENDATION_TARGET_CHUNK = 100;

export type SpKeywordRecommendationTarget = {
  keyword: string;
  matchType: SpMatchType;
  userSelectedKeyword: true;
  bid?: number;
};

export function buildKeywordRecommendationTargets(
  rows: SpAddedKeyword[]
): SpKeywordRecommendationTarget[] {
  return rows.map((row) => {
    const target: SpKeywordRecommendationTarget = {
      keyword: row.keywordText,
      matchType: row.matchType,
      userSelectedKeyword: true,
    };
    const bid = parseFloat(row.bid);
    if (Number.isFinite(bid) && bid > 0) target.bid = bid;
    return target;
  });
}

export function chunkItems<T>(items: T[], size: number): T[][] {
  if (items.length === 0) return [];
  const chunkSize = size > 0 ? size : items.length;
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    out.push(items.slice(i, i + chunkSize));
  }
  return out;
}

export type SuggestedBidLookup = {
  suggestedBid: number | null;
  suggestedBidMin?: number | null;
  suggestedBidMax?: number | null;
};

export function suggestedBidMapFromRecommendations(
  rows: SpKeywordSuggestionRow[]
): Map<string, SuggestedBidLookup> {
  const map = new Map<string, SuggestedBidLookup>();
  for (const row of rows) {
    map.set(keywordRowKey(row.keywordText, row.matchType), {
      suggestedBid: row.suggestedBid,
      suggestedBidMin: row.suggestedBidMin ?? null,
      suggestedBidMax: row.suggestedBidMax ?? null,
    });
  }
  return map;
}

function unwrapSuggestedBid(value: SuggestedBidLookup | number | null | undefined): SuggestedBidLookup {
  if (value && typeof value === 'object') return value;
  return { suggestedBid: value ?? null };
}

export function applySuggestedBidsToAddedKeywords(
  rows: SpAddedKeyword[],
  suggestedByKey: Map<string, SuggestedBidLookup | number | null>,
  options: { useSuggestedBid: boolean; defaultBid: string; forceBid?: boolean }
): SpAddedKeyword[] {
  if (suggestedByKey.size === 0) return rows;
  return rows.map((row) => {
    const key = keywordRowKey(row.keywordText, row.matchType);
    if (!suggestedByKey.has(key)) return row;
    const lookup = unwrapSuggestedBid(suggestedByKey.get(key));
    const suggestedBid = lookup.suggestedBid;
    const next = {
      ...row,
      suggestedBid,
      suggestedBidMin: lookup.suggestedBidMin ?? row.suggestedBidMin ?? null,
      suggestedBidMax: lookup.suggestedBidMax ?? row.suggestedBidMax ?? null,
    };
    if (suggestedBid == null) return next;
    const trimmed = row.bid.trim();
    const bidUnedited = !trimmed || trimmed === options.defaultBid;
    const shouldApplyBid = options.forceBid || (options.useSuggestedBid && bidUnedited);
    const bid = shouldApplyBid ? String(suggestedBid) : row.bid;
    return { ...next, bid };
  });
}

export function parseAsinList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of raw.split(/[\n,\s]+/)) {
    const text = line.trim().toUpperCase();
    if (!text) continue;
    if (seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }
  return out;
}

/** Amazon ASINs are 10-character B-prefixed ids; demo catalog uses the same shape, sometimes longer. */
export function isLikelyAsin(id: string): boolean {
  return /^B[0-9A-Z]{9,}$/i.test(id.trim());
}

export function splitEnteredProductIds(ids: string[]): { asins: string[]; skus: string[] } {
  const asins: string[] = [];
  const skus: string[] = [];
  for (const id of ids) {
    if (isLikelyAsin(id)) asins.push(id.toUpperCase());
    else skus.push(id.toUpperCase());
  }
  return { asins, skus };
}

/** ASIN/SKU lookup in Search — not a title phrase like "bluetooth speaker". */
export function parseSearchQueryAsProductIds(raw: string): string[] | null {
  const ids = parseAsinList(raw);
  if (ids.length === 0) return null;
  if (ids.every(isLikelyAsin)) return ids;
  if (ids.length === 1 && !/\s/.test(raw.trim())) return ids;
  return null;
}

function findProductByAsin(
  products: SpAdvertisedProduct[],
  asin: string
): SpAdvertisedProduct | undefined {
  const key = asin.toUpperCase();
  return products.find((product) => product.asin.toUpperCase() === key);
}

function findProductBySku(
  products: SpAdvertisedProduct[],
  sku: string
): SpAdvertisedProduct | undefined {
  const key = sku.toUpperCase();
  return products.find((product) => product.sku?.toUpperCase() === key);
}

export function isAdvertisedProductAlreadyAdded(
  existing: SpAdvertisedProduct[],
  product: SpAdvertisedProduct
): boolean {
  const key = advertisedProductKey(product);
  if (existing.some((item) => advertisedProductKey(item) === key)) return true;
  const asin = product.asin?.trim().toUpperCase();
  if (asin && existing.some((item) => item.asin.trim().toUpperCase() === asin)) return true;
  const sku = product.sku?.trim().toUpperCase();
  if (sku && existing.some((item) => item.sku?.trim().toUpperCase() === sku)) return true;
  return false;
}

export type EnteredProductLookupResult = {
  toAdd: SpAdvertisedProduct[];
  skippedIneligible: SpAdvertisedProduct[];
  addedWithoutDetails: SpAdvertisedProduct[];
};

export function resolveEnteredAdvertisedProducts(input: {
  ids: string[];
  metadata: SpAdvertisedProduct[];
  catalog: SpAdvertisedProduct[];
  existing: SpAdvertisedProduct[];
}): EnteredProductLookupResult {
  const toAdd: SpAdvertisedProduct[] = [];
  const skippedIneligible: SpAdvertisedProduct[] = [];
  const addedWithoutDetails: SpAdvertisedProduct[] = [];
  const accepted = [...input.existing];

  for (const rawId of input.ids) {
    const id = rawId.trim().toUpperCase();
    if (!id) continue;
    const asAsin = isLikelyAsin(id);
    const matched = asAsin
      ? (findProductByAsin(input.metadata, id) ?? findProductByAsin(input.catalog, id))
      : (findProductBySku(input.metadata, id) ?? findProductBySku(input.catalog, id));
    const product: SpAdvertisedProduct = matched ?? (asAsin ? { asin: id } : { asin: '', sku: id });
    if (isAdvertisedProductIneligible(product)) {
      skippedIneligible.push(product);
      continue;
    }
    if (isAdvertisedProductAlreadyAdded(accepted, product)) continue;
    toAdd.push(product);
    accepted.push(product);
    if (!matched) addedWithoutDetails.push(product);
  }

  return { toAdd, skippedIneligible, addedWithoutDetails };
}

export function formatEnteredProductLookupMessage(
  result: EnteredProductLookupResult
): string | null {
  const parts: string[] = [];
  if (result.addedWithoutDetails.length > 0) {
    const labels = result.addedWithoutDetails.map((product) => product.asin || product.sku).filter(Boolean);
    parts.push(`Added ${labels.join(', ')} without Amazon product details.`);
  }
  if (result.skippedIneligible.length > 0) {
    const labels = result.skippedIneligible.map((product) => product.asin || product.sku).filter(Boolean);
    parts.push(
      `${labels.join(', ')} ${labels.length === 1 ? 'is' : 'are'} ineligible and ${
        labels.length === 1 ? 'was' : 'were'
      } not added.`
    );
  }
  return parts.length > 0 ? parts.join(' ') : result.toAdd.length === 0 ? 'Those products are already added.' : null;
}

export function productsForSearchDropdown(input: {
  query: string;
  catalog: SpAdvertisedProduct[];
  localMatches: SpAdvertisedProduct[];
  idLookup: SpAdvertisedProduct[];
  titleSearch: SpAdvertisedProduct[];
}): SpAdvertisedProduct[] {
  if (!input.query.trim()) return input.catalog;
  if (input.localMatches.length > 0) return input.localMatches;
  const ids = parseSearchQueryAsProductIds(input.query);
  if (!ids) return input.titleSearch;
  const resolved = resolveEnteredAdvertisedProducts({
    ids,
    metadata: input.idLookup,
    catalog: input.catalog,
    existing: [],
  });
  return [...resolved.toAdd, ...resolved.skippedIneligible];
}

export function parseNegativeKeywords(
  raw: string,
  matchType: SpNegativeMatchType
): Array<{ keywordText: string; matchType: SpNegativeMatchType }> {
  return parseKeywordList(raw).map((keywordText) => ({ keywordText, matchType }));
}

export function validateKeywordBids(rows: SpAddedKeyword[]): string | null {
  if (rows.length === 0) return 'Add at least one keyword.';
  for (const row of rows) {
    const bid = parseFloat(row.bid);
    if (Number.isNaN(bid) || bid <= 0) {
      return `Enter a bid for “${row.keywordText}” (${row.matchType.toLowerCase()}).`;
    }
  }
  return null;
}

export function validateProductTargetBids(rows: SpAddedProductTarget[]): string | null {
  if (rows.length === 0) return 'Add at least one category or product target.';
  for (const row of rows) {
    const bid = parseFloat(row.bid);
    if (Number.isNaN(bid) || bid <= 0) {
      return `Enter a bid for “${row.label}”.`;
    }
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function parseProductMetadataPayload(data: unknown): SpAdvertisedProduct[] {
  const root = asRecord(data);
  if (!root) return [];
  const list =
    (Array.isArray(root.products) && root.products) ||
    (Array.isArray(root.ProductMetadataList) && root.ProductMetadataList) ||
    (Array.isArray(root.productMetadataList) && root.productMetadataList) ||
    [];
  const out: SpAdvertisedProduct[] = [];
  for (const item of list) {
    const rec = asRecord(item);
    if (!rec) continue;
    const asin = asString(rec.asin) ?? asString(rec.ASIN);
    if (!asin) continue;
    const priceToPay = asRecord(rec.priceToPay);
    const price = (priceToPay ? asNumber(priceToPay.amount) : null) ?? asNumber(rec.price);
    const reviews = asRecord(rec.customerReviews);
    const rating = asNumber(rec.rating) ?? (reviews ? asNumber(reviews.rating) : null);
    const availability = asString(rec.availability);
    out.push({
      asin,
      sku: asString(rec.sku) ?? asString(rec.SKU),
      title: asString(rec.title) ?? asString(rec.productTitle),
      imageUrl: asString(rec.imageUrl) ?? asString(rec.imageURL),
      eligibilityStatus: asString(rec.eligibilityStatus) ?? asString(rec.eligibility),
      ...(price != null ? { price } : {}),
      ...(availability ? { availability } : {}),
      ...(rating != null ? { rating } : {}),
    });
  }
  return out;
}

function parseMatchType(value: unknown): SpMatchType | null {
  const matchType = asString(value)?.toUpperCase();
  if (matchType === 'EXACT' || matchType === 'PHRASE' || matchType === 'BROAD') return matchType;
  return null;
}

/**
 * Amazon SP keyword recommendations v5 (`/sp/targets/keywords/recommendations`)
 * returns bid fields in minor currency units (paise for INR, cents for USD).
 * Keyword create/update expects major units (rupees / dollars).
 */
export function normalizeKeywordRecommendationBid(raw: number): number {
  return Math.round(raw) / 100;
}

function parseSuggestedBidValue(info: Record<string, unknown>): number | null {
  const suggested = asRecord(info.suggestedBid);
  const raw =
    (suggested ? asNumber(suggested.suggested) : null) ??
    (suggested ? asNumber(suggested.rangeMedian) : null) ??
    asNumber(info.bid);
  if (raw == null) return null;
  return normalizeKeywordRecommendationBid(raw);
}

function parseSuggestedBidBounds(info: Record<string, unknown>): {
  suggestedBidMin: number | null;
  suggestedBidMax: number | null;
} {
  const suggested = asRecord(info.suggestedBid);
  const minRaw = suggested ? asNumber(suggested.rangeStart) : null;
  const maxRaw = suggested ? asNumber(suggested.rangeEnd) : null;
  return {
    suggestedBidMin: minRaw == null ? null : normalizeKeywordRecommendationBid(minRaw),
    suggestedBidMax: maxRaw == null ? null : normalizeKeywordRecommendationBid(maxRaw),
  };
}

function parseKeywordRecommendationItem(rec: Record<string, unknown>): SpKeywordSuggestionRow[] {
  const keywordText = asString(rec.keyword) ?? asString(rec.keywordText);
  if (!keywordText) return [];
  const bidInfo = Array.isArray(rec.bidInfo) ? rec.bidInfo : [];
  if (bidInfo.length > 0) {
    const rows: SpKeywordSuggestionRow[] = [];
    for (const info of bidInfo) {
      const infoRec = asRecord(info);
      if (!infoRec) continue;
      const matchType = parseMatchType(infoRec.matchType);
      if (!matchType) continue;
      rows.push({ keywordText, matchType, suggestedBid: parseSuggestedBidValue(infoRec), ...parseSuggestedBidBounds(infoRec) });
    }
    return rows;
  }
  const matchType = parseMatchType(rec.matchType);
  if (!matchType) return [];
  return [{ keywordText, matchType, suggestedBid: parseSuggestedBidValue(rec), ...parseSuggestedBidBounds(rec) }];
}

export function parseKeywordRecommendationsPayload(data: unknown): SpKeywordSuggestionRow[] {
  const items: unknown[] = [];
  if (Array.isArray(data)) {
    items.push(...data);
  } else {
    const root = asRecord(data);
    if (!root) return [];
    if (Array.isArray(root.keywordTargetList)) items.push(...root.keywordTargetList);
    else if (asString(root.keyword) || asString(root.keywordText)) items.push(root);
  }
  const rows: SpKeywordSuggestionRow[] = [];
  for (const item of items) {
    const rec = asRecord(item);
    if (!rec) continue;
    rows.push(...parseKeywordRecommendationItem(rec));
  }
  return rows;
}

function formatCountRange(min: number | null, max: number | null): string | undefined {
  if (min != null && max != null && min !== max) {
    return `${min.toLocaleString()}–${max.toLocaleString()}`;
  }
  if (min != null) return min.toLocaleString();
  if (max != null) return max.toLocaleString();
  return undefined;
}

function categoryProductCountLabel(rec: Record<string, unknown>): string | undefined {
  const counts = asRecord(rec.asinCounts) ?? asRecord(rec.productCounts);
  const min =
    asNumber(rec.productCountMin) ??
    asNumber(rec.asinCountLowerBound) ??
    (counts ? asNumber(counts.min) ?? asNumber(counts.lowerBound) : null);
  const max =
    asNumber(rec.productCountMax) ??
    asNumber(rec.asinCountUpperBound) ??
    (counts ? asNumber(counts.max) ?? asNumber(counts.upperBound) : null);
  const single = asNumber(rec.productCount) ?? asNumber(rec.asinCount);
  return formatCountRange(min ?? single, max ?? single);
}

export function parseCategoryRecommendationsPayload(data: unknown): SpCategorySuggestion[] {
  const root = asRecord(data);
  if (!root) return [];
  const list = Array.isArray(root.categories) ? root.categories : [];
  const out: SpCategorySuggestion[] = [];
  for (const item of list) {
    const rec = asRecord(item);
    if (!rec) continue;
    const id = asString(rec.id);
    if (!id) continue;
    const productCountLabel = categoryProductCountLabel(rec);
    out.push({
      id,
      name: asString(rec.name) ?? id,
      path: asString(rec.path) ?? asString(rec.name) ?? id,
      canBeTargeted: rec.canBeTargeted !== false,
      suggestedBid: asNumber(rec.suggestedBid),
      bidMin: asNumber(rec.bidMin),
      bidMax: asNumber(rec.bidMax),
      ...(productCountLabel ? { productCountLabel } : {}),
    });
  }
  return out;
}

export function parseProductEligibilityMap(data: unknown): Map<string, string> {
  const map = new Map<string, string>();
  const root = asRecord(data);
  if (!root) return map;
  const list =
    (Array.isArray(root.productResponseList) && root.productResponseList) ||
    (Array.isArray(root.productDetailsList) && root.productDetailsList) ||
    [];
  for (const item of list) {
    const rec = asRecord(item);
    if (!rec) continue;
    const asin = asString(rec.asin)?.toUpperCase();
    if (!asin) continue;
    const statuses = Array.isArray(rec.eligibilityStatusList) ? rec.eligibilityStatusList : [];
    let status = asString(rec.eligibilityStatus);
    for (const entry of statuses) {
      const entryRec = asRecord(entry);
      if (!entryRec) continue;
      const adType = asString(entryRec.adType)?.toUpperCase();
      if (adType && adType !== 'SP') continue;
      status = asString(entryRec.eligibilityStatus) ?? status;
    }
    if (status) map.set(asin, status);
  }
  return map;
}

export function parseProductRecommendationsPayload(data: unknown): string[] {
  const root = asRecord(data);
  if (!root) return [];
  const list = Array.isArray(root.recommendations)
    ? root.recommendations
    : Array.isArray(root.recommendedProducts)
      ? root.recommendedProducts
      : [];
  const asins: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    const rec = asRecord(item);
    if (!rec) continue;
    const asin = (asString(rec.recommendedAsin) ?? asString(rec.recommendedTargetAsin))?.toUpperCase();
    if (!asin || seen.has(asin)) continue;
    seen.add(asin);
    asins.push(asin);
  }
  return asins;
}

export function parseBidRecommendationMap(data: unknown): Map<string, number> {
  const map = new Map<string, number>();
  const root = asRecord(data);
  if (!root) return map;
  const recs = Array.isArray(root.bidRecommendations) ? root.bidRecommendations : [];
  for (const rec of recs) {
    const recObj = asRecord(rec);
    if (!recObj) continue;
    const expressions = Array.isArray(recObj.bidRecommendationsForTargetingExpressions)
      ? recObj.bidRecommendationsForTargetingExpressions
      : [];
    for (const expr of expressions) {
      const exprObj = asRecord(expr);
      if (!exprObj) continue;
      const targeting = asRecord(exprObj.targetingExpression);
      const type = asString(targeting?.type);
      const value = asString(targeting?.value) ?? '';
      const bids = Array.isArray(exprObj.bidValues) ? exprObj.bidValues : [];
      const mid = asRecord(bids[Math.min(1, Math.max(0, bids.length - 1))]) ?? asRecord(bids[0]);
      const suggested = asNumber(mid?.suggestedBid);
      if (!type || suggested == null) continue;
      map.set(productTargetRowKey(type, value), suggested);
    }
  }
  return map;
}

export function extractGenericMutationResults(
  data: unknown,
  idKey: string
): { errors: string[]; successIds: string[] } {
  const errors: string[] = [];
  const successIds: string[] = [];
  const root = asRecord(data);
  if (!root) return { errors, successIds };
  const results = Array.isArray(root.results) ? root.results : [];
  for (const item of results) {
    const rec = asRecord(item);
    if (!rec) continue;
    const status = asString(rec.status)?.toLowerCase();
    const id = rec[idKey];
    if (status === 'success' && (typeof id === 'string' || typeof id === 'number')) {
      successIds.push(String(id));
    } else if (status === 'error') {
      errors.push(typeof rec.message === 'string' ? rec.message : 'Create failed.');
    }
  }
  return { errors, successIds };
}

export function formatPlacementPreview(defaultBid: string, placements: ReturnType<typeof buildPlacementBidding>): string {
  const bid = parseFloat(defaultBid);
  const base = Number.isFinite(bid) && bid > 0 ? bid : 0;
  const byPlacement = new Map(placements.map((p) => [p.placement, p.percentage]));
  const amount = (key: string) => {
    const pct = byPlacement.get(key) ?? 0;
    return (base * (1 + pct / 100)).toFixed(2);
  };
  const money = (raw: string) => formatCurrency(Number(raw));
  return `A ${formatCurrency(base)} bid will start at ${money(amount('PLACEMENT_TOP'))} for top of search, ${money(amount('PLACEMENT_REST_OF_SEARCH'))} for rest of search and ${money(amount('PLACEMENT_PRODUCT_PAGE'))} for product pages.`;
}

export function buildProductAdPayload(
  campaignId: string,
  adGroupId: string,
  product: SpAdvertisedProduct
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    campaignId,
    adGroupId,
    state: 'ENABLED',
  };
  if (product.sku) payload.sku = product.sku;
  if (product.asin) payload.asin = product.asin;
  return payload;
}
